<template>
  <vxe-table
    v-if="isLargeScreen"
    class="filters-table"
    empty-text="No data"
    border="none"
    :data="slicedFilterData"
    :span-method="mergeRowMethod"
    :sort-config="{ sortMethod: customSortMethod }"
    :cell-class-name="cellClassName"
    @cell-click="clickCellEvent"
  >
    <vxe-column field="filterType" title="Filter type" width="30%"></vxe-column>
    <template v-if="alertFiltersType === FILTER_TYPE.PREFIX">
      <vxe-column
        type="html"
        field="prefixStatuses"
        title="Prefix number(s)"
        width="70%"
        :formatter="formatFilter"
      ></vxe-column>
    </template>
    <template v-else-if="alertFiltersType === FILTER_TYPE.AS">
      <vxe-column
        type="html"
        field="originStatuses"
        title="Origin AS number(s)"
        width="70%"
        :formatter="formatFilter"
      ></vxe-column>
    </template>
  </vxe-table>

  <div v-else class="mobile">
    <div class="wrapper first">
      <span>Filter type</span>
      <span>{{ processedFilterDataList[0].filterType }}</span>
    </div>
    <div v-if="alertFiltersType === FILTER_TYPE.PREFIX" class="wrapper last">
      <span>Prefix number(s)</span>
      <div>
        <ul>
          <li v-for="(filter, index) in slicedFilterData" :key="index">
            {{ filter.prefixStatuses }}
          </li>
        </ul>
        <span v-if="isFiltersSliced" class="show-more" @click="showFilterList"
          >and {{ slicedFilterAmount }} more</span
        >
      </div>
    </div>
    <div v-if="alertFiltersType === FILTER_TYPE.AS" class="wrapper last">
      <span>Origin AS(es)</span>
      <div>
        <ul>
          <li v-for="(filter, index) in slicedFilterData" :key="index">
            {{ filter.originStatuses }}
          </li>
        </ul>
        <span v-if="isFiltersSliced" class="show-more" @click="showFilterList"
          >and {{ slicedFilterAmount }} more</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

import {
  FILTER_TYPE,
  MAX_FILTERS_SHOWN,
  SHOW_MORE_MODAL_TYPE
} from '../../../../constants/alertsRoutingStatusConstant';
import {
  sortPrefixByAscending,
  sortPrefixByDescending,
  sortASNByAscending,
  sortASNByDescending
} from '../../../../utils/ipAddressHelpers';

export default {
  name: 'AlertRSFiltersTable',
  components: {},
  props: ['alertFiltersType', 'filterDataList', 'alertId'],
  data() {
    return {
      FILTER_TYPE: FILTER_TYPE,
      sortType: 0,
      isFiltersSliced: false,
      slicedFilterAmount: 0,
      MAX_FILTERS_SHOWN: MAX_FILTERS_SHOWN
    };
  },
  computed: {
    ...mapGetters(['isLargeScreen']),
    processedFilterDataList() {
      let list = this.filterDataList;

      // deep clone list as list.sort will change the origin list
      list = JSON.parse(JSON.stringify(list));

      list.sort((a, b) => {
        if (a.prefixStatuses) {
          return sortPrefixByAscending(a.prefixStatuses, b.prefixStatuses);
        } else if (a.originStatuses) {
          return sortASNByAscending(a.originStatuses, b.originStatuses);
        } else {
          return 0;
        }
      });

      return list;
    },
    slicedFilterData() {
      if (this.processedFilterDataList.length > MAX_FILTERS_SHOWN) {
        this.isFiltersSliced = true;
        this.slicedFilterAmount =
          this.processedFilterDataList.length - MAX_FILTERS_SHOWN;

        return this.processedFilterDataList.slice(0, MAX_FILTERS_SHOWN);
      } else {
        this.isFiltersSliced = false;
        this.slicedFilterAmount = 0;

        return this.processedFilterDataList;
      }
    }
  },
  methods: {
    ...mapMutations('alertsRoutingStatusStore', ['setShowMoreModalObj']),

    mergeRowMethod({ row, _rowIndex, column, visibleData }) {
      const fields = ['filterType'];
      const cellValue = row[column.property];
      if (cellValue && fields.includes(column.property)) {
        const prevRow = visibleData[_rowIndex - 1];
        let nextRow = visibleData[_rowIndex + 1];
        if (prevRow && prevRow[column.property] === cellValue) {
          return { rowspan: 0, colspan: 0 };
        } else {
          let countRowspan = 1;
          while (nextRow && nextRow[column.property] === cellValue) {
            nextRow = visibleData[++countRowspan + _rowIndex];
          }
          if (countRowspan > 1) {
            return { rowspan: countRowspan, colspan: 1 };
          }
        }
      }
    },

    customSortMethod({ data, sortList }) {
      const { property, order } = sortList[0];
      let list = [];
      if (property === 'prefixStatuses') {
        if (order === 'asc') {
          list = data.sort((a, b) => {
            return sortPrefixByAscending(a.prefixStatuses, b.prefixStatuses);
          });
        } else if (order === 'desc') {
          list = data.sort((a, b) => {
            return sortPrefixByDescending(a.prefixStatuses, b.prefixStatuses);
          });
        } else {
          list = data;
        }
      } else if (property === 'originStatuses') {
        if (order === 'asc') {
          list = data.sort((a, b) => {
            return sortASNByAscending(a.originStatuses, b.originStatuses);
          });
        } else if (order == 'desc') {
          list = data.sort((a, b) => {
            return sortASNByDescending(a.originStatuses, b.originStatuses);
          });
        } else {
          list = data;
        }
      }

      return list;
    },

    cellClassName(event) {
      const { column } = event;

      if (
        (column.property === 'prefixStatuses' ||
          column.property === 'originStatuses') &&
        this.isFiltersSliced
      ) {
        return 'pointer';
      }
    },

    clickCellEvent(event) {
      const { column } = event;

      if (
        (column.field === 'prefixStatuses' ||
          column.field === 'originStatuses') &&
        this.isFiltersSliced
      ) {
        this.showFilterList();
      }
    },

    formatFilter({ cellValue, rowIndex }) {
      if (this.isFiltersSliced && this.slicedFilterAmount) {
        if (rowIndex === MAX_FILTERS_SHOWN - 1) {
          return `<span>${cellValue}<br /><span class="show-more">and ${
            this.slicedFilterAmount
          } more</span></span>`;
        } else {
          return cellValue;
        }
      } else {
        return cellValue;
      }
    },

    showFilterList() {
      let bodyTextOfFilters = '';

      if (this.alertFiltersType === FILTER_TYPE.PREFIX) {
        bodyTextOfFilters = this.processedFilterDataList
          .map(i => i.prefixStatuses)
          .join(', ');
      } else if (this.alertFiltersType === FILTER_TYPE.AS) {
        bodyTextOfFilters = this.processedFilterDataList
          .map(i => i.originStatuses)
          .join(', ');
      }

      this.setShowMoreModalObj({
        isShown: true,
        bodyTextOfFilters: bodyTextOfFilters,
        modalType: SHOW_MORE_MODAL_TYPE.FILTERS,
        alertId: this.alertId
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.filters-table {
  /deep/ .vxe-header--row {
    background-color: #ffffff !important;
    background-image: none !important;
  }

  /deep/ td.vxe-body--column {
    background-image: none;
    padding: 0.25rem 0;
  }

  /deep/ .vxe-table--body {
    padding: 0.5rem 0;
  }

  /deep/ td {
    vertical-align: top !important;
  }

  /deep/ .pointer {
    cursor: pointer;
  }

  /deep/ .show-more {
    display: inline-block;
    margin-top: 0.5rem;
    color: $apnic-blue;
    cursor: pointer;
  }
}

.mobile {
  padding-bottom: 0.5rem;

  ul {
    list-style: none;
  }

  li {
    margin-bottom: 0.75rem;
  }

  .wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    margin-top: 1rem;
    margin-bottom: 1.25rem;

    &.first {
      border-top: 1px solid #666;
      margin-top: 0.5rem;
      padding-top: 1rem;
    }
    &.last {
      border-bottom: 1px solid #666;
      padding-bottom: 0.5rem;
      margin-bottom: 1rem;

      .show-more {
        position: relative;
        top: -0.5rem;
        color: $apnic-blue;
        cursor: pointer;
      }
    }
  }

  .wrapper span:nth-child(1) {
    width: 8rem;
    min-width: 8rem;
    color: $text-black;
    font-weight: 600;
  }
}
</style>
